import type { TErrorLogSource } from '@/services/errorLog';

import { errorLogService } from '@/services/errorLog';
import { toBounded, toErrorOrigin } from '@/utils/errorLog';

import type { TErrorReport } from './errorLog.types';

const REPEAT_WINDOW_MS = 30_000;

const MAX_REPORTS_PER_PAGE = 50;

const LIMITS = {
  token: 64,
  message: 500,
  route: 500,
  stack: 4000,
  userAgent: 500,
} as const;

const FALLBACK_ACTION: Record<TErrorLogSource, string> = {
  mutation: 'unknown_mutation',
  query: 'unknown_query',
  window: 'window_error',
  render: 'route_error',
};

const FALLBACK_MESSAGE = 'Unexpected error';

const lastReportedAt = new Map<string, number>();

let isEnabled = false;

let reportCount = 0;

const enable = (): void => {
  isEnabled = true;
};

const disable = (): void => {
  isEnabled = false;
};

const isRepeated = (key: string): boolean => {
  const now = Date.now();
  const previous = lastReportedAt.get(key);

  if (previous !== undefined && now - previous < REPEAT_WINDOW_MS) {
    return true;
  }

  lastReportedAt.set(key, now);

  return false;
};

const report = (input: TErrorReport): void => {
  if (!isEnabled || reportCount >= MAX_REPORTS_PER_PAGE || typeof window === 'undefined') {
    return;
  }

  const origin = toErrorOrigin(input.where, input.key, input.action ?? FALLBACK_ACTION[input.source]);
  const code = toBounded(input.code, LIMITS.token);

  if (isRepeated(`${input.source}:${origin.action}:${code ?? ''}`)) {
    return;
  }

  reportCount += 1;

  const entityID = origin.entityKind ? input.entityID ?? null : null;

  errorLogService.recordError({
    action: origin.action,
    entityKind: origin.entityKind,
    entityID,
    code,
    message: toBounded(input.message, LIMITS.message) ?? FALLBACK_MESSAGE,
    source: input.source,
    route: toBounded(window.location.pathname, LIMITS.route),
    stack: toBounded(input.stack, LIMITS.stack),
    release: toBounded(import.meta.env.VITE_APP_RELEASE, LIMITS.token),
    userAgent: toBounded(window.navigator.userAgent, LIMITS.userAgent),
  });
};

const errorLog = {
  enable,
  disable,
  report,
};

export {
  errorLog,
};
